import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { RoleLoginShell } from '@/components/auth/RoleLoginShell';
import { ROLE_LOGIN_THEMES } from '@/components/auth/roleLoginThemes';
import { resetPassword } from '@/api/auth';

const THEME = ROLE_LOGIN_THEMES["login"];

/**
 * Landing page for the emailed password-reset link
 * (`/reset-password?token=…`). Redeems the single-use token together with the
 * new password; the backend revokes every other session on success.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset the password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const meta = (
    <p className="rl__meta-row">
      Link expired or not working? <Link to="/forgot-password">Request a new link</Link>
    </p>
  );

  if (!token) {
    return (
      <RoleLoginShell theme={THEME} meta={meta}>
        <div className="rl__error" role="alert">
          This link is missing its token. Please open the link from your email again.
        </div>
        <Link className="rl__submit-link" to="/login">
          Go to sign in
        </Link>
      </RoleLoginShell>
    );
  }

  if (done) {
    return (
      <RoleLoginShell theme={THEME}>
        <div className="rl__notice" role="status">
          Your password has been updated. Any other signed-in devices have been signed out.
        </div>
        <Link className="rl__submit-link" to="/login">
          Sign in
        </Link>
      </RoleLoginShell>
    );
  }

  return (
    <RoleLoginShell theme={THEME} meta={meta}>
      <p className="rl__otp-note">Choose a new password for your account.</p>

      {error && <div className="rl__error" role="alert">{error}</div>}

      <form className="rl__form" onSubmit={handleSubmit}>
        <div className="rl__field">
          <label className="rl__label" htmlFor="reset-password">New password</label>
          <div className="rl__password-wrap">
            <input id="reset-password" className="rl__input"
              type={showPassword ? "text" : "password"} value={password}
              onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
              required autoFocus minLength={8} autoComplete="new-password" />
            <button type="button" className="rl__password-toggle"
              onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div className="rl__field">
          <label className="rl__label" htmlFor="reset-confirm">Confirm password</label>
          <input id="reset-confirm" className="rl__input"
            type={showPassword ? "text" : "password"} value={confirm}
            onChange={e => setConfirm(e.target.value)} placeholder="Repeat the new password"
            required minLength={8} autoComplete="new-password" />
        </div>
        <button type="submit" className="rl__submit" disabled={loading || !password || !confirm}>
          {loading ? <><Loader2 size={16} className="rl__spin" /> Saving...</> : "Reset Password"}
        </button>
      </form>
    </RoleLoginShell>
  );
}
